import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft } from 'lucide-react';
import { createWorld } from './world/index.js';
import type { LocationDef } from '#entities/location/index.js';
import { Stalker, PlayerID, Location, Finding, Rarity, WorldState } from '@stalker/core';
import { soundEngine } from '#shared/lib/sound-engine.js';
import type { SoundId } from '#shared/lib/sound-engine.js';
import { ITEM_MAP, getItemView } from '#shared/config/items.js';
import { PlayerHud } from '#widgets/player-hud/index.js';
import { PdaLog, type PdaMessage } from '#widgets/pda-log/index.js';
import { CityPage } from '#pages/city/index.js';
import { MapPage } from '#pages/map/index.js';
import { LocationPage } from '#pages/location/index.js';
import { RoomPage } from '#pages/room/index.js';
import { InventoryPage } from '#pages/inventory/index.js';
import { TgBackButton } from '#shared/ui/telegram/TgBackButton.js';
import { platform } from '#shared/lib/platform/index.js';

type GameScreen = 'city' | 'map' | 'location' | 'room' | 'inventory';

const App: React.FC = () => {
  const [world] = useState(() => createWorld());
  const [player] = useState(() => new Stalker(
    new PlayerID('stalker-1'),
    world.city.core,
    1.0,
    0.0
  ));

  const [screen, setScreen] = useState<GameScreen>('city');
  const [selectedStreet, setSelectedStreet] = useState<LocationDef | null>(null);
  const [selectedLoc, setSelectedLoc] = useState<LocationDef | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Location | null>(null);
  const [worldState, setWorldState] = useState<WorldState>({
    currentStormId: 'initial',
    lastStormAt: Date.now()
  });
  const [messages, setMessages] = useState<PdaMessage[]>([
    { timestamp: new Date().toISOString(), level: 2, message: 'PDA: Соединение установлено.' },
    { timestamp: new Date().toISOString(), level: 1, message: 'Добро пожаловать в Низину.' }
  ]);
  const [tick, setTick] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const [soundInitialized, setSoundInitialized] = useState(false);

  const initSound = () => {
    if (!soundInitialized) {
      soundEngine.init();
      soundEngine.resume();
      soundEngine.startAmbient();
      setSoundInitialized(true);
      setSoundOn(true);
      addMessage('PDA: Аудио-модуль откалиброван.', 2);
    }
  };

  const toggleSound = () => {
    initSound();
    const muted = soundEngine.toggleMute();
    setSoundOn(!muted);
  };

  const playSound = useCallback((id: SoundId) => {
    if (soundOn && soundInitialized) soundEngine.play(id);
  }, [soundOn, soundInitialized]);

  // Игровой цикл
  useEffect(() => {
    const interval = setInterval(() => {
      player.onTick(100);
      setTick(t => t + 1);

      if (soundInitialized && soundOn) {
        soundEngine.updateGeiger(player.radiation);
        if (player.health < 0.3) {
          soundEngine.updateHeartbeat(1.0 - player.health);
        } else {
          soundEngine.stopHeartbeat();
        }
      }

      if (player.isDead) {
        addMessage('PDA: КРИТИЧЕСКИЙ УРОВЕНЬ РАДИАЦИИ. СВЯЗЬ ПРЕРВАНА...', 3);
        playSound('death');
        platform.haptic.notification('error');
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [player, soundInitialized, soundOn]);

  useEffect(() => {
    Object.assign(window, { game: { world, player, soundEngine } });
  }, [player, world]);

  const addMessage = useCallback((msg: string, level: 1 | 2 | 3 = 1) => {
    setMessages(prev => [...prev, {
      timestamp: new Date().toISOString(),
      level,
      message: msg
    }]);
  }, []);

  const triggerStorm = () => {
    const newStormId = `storm-${Math.random().toString(36).slice(2, 7)}`;
    setWorldState({
      currentStormId: newStormId,
      lastStormAt: Date.now()
    });
    addMessage("PDA: ВНИМАНИЕ! РАДИОАКТИВНЫЙ ВЫБРОС! Локации обновлены.", 3);
    playSound('ui_error');
  };

  const handleEnterStreet = (def: LocationDef) => {
    try {
      platform.haptic.selectionChanged();
      world.lootGenerator.refreshIfNeeded(def.core, worldState);
      player.moveTo(def.core);
      setSelectedStreet(def);
      setScreen('map');
      addMessage(`PDA: Переход на ${def.name}`, 2);
      playSound('footstep');

      if (soundInitialized && soundOn && def.soundPreset) {
        soundEngine.applyPreset(def.soundPreset);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      if (message.includes('energy')) {
        addMessage(`PDA: ВНИМАНИЕ! НЕДОСТАТОЧНО ЭНЕРГИИ ДЛЯ ПЕРЕМЕЩЕНИЯ`, 3);
      } else {
        addMessage(`СИСТЕМА: ${message}`, 3);
      }
      playSound('ui_error');
      platform.haptic.notification('warning');
    }
  };

  const handleEnterLocation = (def: LocationDef) => {
    try {
      platform.haptic.selectionChanged();
      world.lootGenerator.refreshIfNeeded(def.core, worldState);
      player.moveTo(def.core);
      setSelectedLoc(def);
      setScreen('location');
      addMessage(`PDA: Вход в ${def.name}`, 2);
      playSound('door_open');

      if (soundInitialized && soundOn && def.soundPreset) {
        soundEngine.applyPreset(def.soundPreset);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      if (message.includes('energy')) {
        addMessage(`PDA: ВНИМАНИЕ! НЕДОСТАТОЧНО ЭНЕРГИИ ДЛЯ ПЕРЕМЕЩЕНИЯ`, 3);
      } else {
        addMessage(`СИСТЕМА: ${message}`, 3);
      }
      playSound('ui_error');
      platform.haptic.notification('warning');
    }
  };

  const handleEnterRoom = (room: Location) => {
    try {
      platform.haptic.selectionChanged();
      world.lootGenerator.refreshIfNeeded(room, worldState);
      player.moveTo(room);
      setSelectedRoom(room);
      setScreen('room');
      const roomDef = Object.values(world.locations).find(l => l.core === room);
      const name = roomDef?.name ?? room.id.toString();
      addMessage(`PDA: Исследование объекта ${name}`, 2);
      playSound('footstep');

      if (soundInitialized && soundOn && roomDef?.soundPreset) {
        soundEngine.applyPreset(roomDef.soundPreset);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      if (message.includes('energy')) {
        addMessage(`PDA: ВНИМАНИЕ! НЕДОСТАТОЧНО ЭНЕРГИИ ДЛЯ ПЕРЕМЕЩЕНИЯ`, 3);
      } else {
        addMessage(`СИСТЕМА: ${message}`, 3);
      }
      playSound('ui_error');
      platform.haptic.notification('warning');
    }
  };

  const handlePickItem = (item: Finding) => {
    player.pickFinding(item.id);
    const view = getItemView(item.typeId);
    addMessage(`PDA: Предмет получен: ${view.emoji} ${view.name}`, 2);

    switch (item.rarity) {
      case Rarity.Common:
        playSound('found_common');
        platform.haptic.impact('light');
        break;
      case Rarity.Uncommon:
        playSound('found_uncommon');
        platform.haptic.impact('medium');
        break;
      case Rarity.Rare:
        playSound('found_rare');
        platform.haptic.impact('heavy');
        break;
      case Rarity.Legendary:
        playSound('found_legendary');
        platform.haptic.notification('success');
        break;
      default:
        playSound('found_common');
        platform.haptic.impact('light');
    }

    setTick(t => t + 1);
  };

  const goBack = useCallback(() => {
    try {
      platform.haptic.selectionChanged();
      if (screen === 'room') {
        player.moveTo(selectedLoc!.core);
        setScreen('location');
        playSound('footstep');
        if (soundInitialized && soundOn && selectedLoc?.soundPreset) {
          soundEngine.applyPreset(selectedLoc.soundPreset);
        }
      } else if (screen === 'location') {
        player.moveTo(selectedStreet!.core);
        setScreen('map');
        playSound('door_close');
        if (soundInitialized && soundOn && selectedStreet?.soundPreset) {
          soundEngine.applyPreset(selectedStreet.soundPreset);
        }
      } else if (screen === 'map') {
        player.moveTo(world.city.core);
        setScreen('city');
        playSound('footstep');
      } else if (screen === 'inventory') {
        if (selectedRoom) setScreen('room');
        else if (selectedLoc) setScreen('location');
        else if (selectedStreet) setScreen('map');
        else setScreen('city');
        playSound('ui_click');
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e);
      if (message.includes('energy')) {
        addMessage(`PDA: ВНИМАНИЕ! НЕДОСТАТОЧНО ЭНЕРГИИ ДЛЯ ВЫХОДА`, 3);
      } else {
        addMessage(`СИСТЕМА: ${message}`, 3);
      }
      playSound('ui_error');
    }
  }, [screen, selectedLoc, selectedStreet, selectedRoom, soundInitialized, soundOn, player, world.city.core, playSound, addMessage]);

  const getItemEmoji = (typeId: string) => {
    return ITEM_MAP[typeId]?.emoji ?? '📦';
  };

  const getItemName = (typeId: string) => {
    return ITEM_MAP[typeId]?.name ?? 'Неизвестно';
  };

  return (
    <div className="flex-1 flex flex-col selection:bg-primary/30 selection:text-primary-foreground font-mono">
      {platform.isTelegram && screen !== 'city' && <TgBackButton onClick={goBack} />}
      
      {/* Background Ambience Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(146,64,14,0.1),transparent_70%)]" />
      </div>

      <PlayerHud
        player={player}
        user={platform.user}
        soundOn={soundOn}
        isInventoryActive={screen === 'inventory'}
        onCityClick={() => setScreen('city')}
        onToggleSound={toggleSound}
        onInventoryClick={() => { setScreen('inventory'); playSound('ui_click'); }}
      />

      <main className="flex-1 relative z-10 p-4 md:p-8 max-w-6xl mx-auto w-full">
        {screen === 'city' && (
          <CityPage
            cityName={world.city.name}
            cityDescription={world.city.description}
            streets={world.city.children
              .map(id => world.locations[id])
              .filter((s): s is LocationDef => s !== undefined)}
            onEnterStreet={handleEnterStreet}
          />
        )}

        {screen === 'map' && selectedStreet && (
          <MapPage
            street={selectedStreet}
            buildings={selectedStreet.children
              .map(id => world.locations[id])
              .filter((b): b is LocationDef => b !== undefined)}
            onEnterLocation={handleEnterLocation}
            onBack={goBack}
          />
        )}

        {screen === 'location' && selectedLoc && (
          <LocationPage
            location={selectedLoc}
            rooms={selectedLoc.children
              .map(id => world.locations[id])
              .filter((r): r is LocationDef => r !== undefined)}
            onEnterRoom={handleEnterRoom}
            onBack={goBack}
          />
        )}

        {screen === 'room' && selectedRoom && (
          <RoomPage
            selectedRoom={selectedRoom}
            handlePickItem={handlePickItem}
            goBack={goBack}
            getItemEmoji={getItemEmoji}
            getItemName={getItemName}
          />
        )}

        {screen === 'inventory' && (
          <InventoryPage
            player={player}
            goBack={goBack}
            getItemEmoji={getItemEmoji}
            getItemName={getItemName}
          />
        )}
      </main>

      <PdaLog
        messages={messages}
        zone={selectedLoc?.name ?? 'SECTOR_0'}
      />

      {/* Floating Back Button - Only for Browser, hidden in Telegram */}
      {!platform.isTelegram && (['map', 'location', 'room', 'inventory'] as GameScreen[]).includes(screen) && (
        <button
          onClick={goBack}
          aria-label="Назад"
          className="fixed bottom-16 right-10 z-50 w-12 h-12 rounded-full flex items-center justify-center text-primary bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-primary hover:text-primary-foreground active:scale-90 transition-all shadow-xl"
        >
          <ChevronLeft size={20} />
        </button>
      )}
    </div>
  );
};

export default App;
